<template>
    <div :class="gaugeMergedClasses">
        <svg viewBox="0 0 200 100" :style="{ bottom: `${options.chartLineWeight / 2}px` }" class="w-full h-full relative" overflow="visible" vector-effect="non-scaling-stroke">
            <!-- Background Arc -->
            <path :d="backgroundArcPath" class="stroke-neutral-200" fill="none" :stroke-width="options.chartLineWeight" stroke-linecap="round" vector-effect="non-scaling-stroke" />
            <!-- Value Arc -->
            <path :d="arcPath" :class="gaugeColor" fill="none" :stroke-width="options.chartLineWeight" stroke-linecap="round" vector-effect="non-scaling-stroke" />
        </svg>
        <div :class="textMergedClasses">
            <span>{{ value }}</span>
            <sup v-if="options.suffix">{{ options.suffix }}</sup>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { twMerge } from 'tailwind-merge'
    import { AlertStates } from '../../types/charts.js'

    type FullOptions = {
        chartLineWeight: number
        suffix: string
        textClass: string
    }
    type Options = Partial<FullOptions>

    const OPTIONS_DEFAULT: FullOptions = {
        chartLineWeight: 20,
        suffix: '°',
        textClass: ''
    } as const

    const props = withDefaults(
        defineProps<{
            class?: string
            value: number
            min?: number
            max?: number
            options?: Options
            alertLevel?: -1 | 0 | 1 | 2
        }>(),
        {
            value: 0,
            min: -20,
            max: 50,
            options: () => ({}),
            class: '',
            alertLevel: 0
        }
    )

    const options = computed<FullOptions>(() => ({
        ...OPTIONS_DEFAULT,
        ...props.options
    }))

    const alertState = computed(() => {
        if (props.alertLevel === 0) return AlertStates.Success
        else if (props.alertLevel === 1) return AlertStates.Warning
        else if (props.alertLevel === 2) return AlertStates.Error
        else return AlertStates.Neutral
    })

    const gaugeColor = computed(() => {
        const baseClasses = 'transition-colors duration-100'
        const alertStates = {
            neutral: 'stroke-neutral-400',
            success: 'stroke-green-500',
            warning: 'stroke-yellow-500',
            error: 'stroke-red-500'
        }
        return [baseClasses, alertStates[alertState.value]]
    })

    const percentage = computed(() => {
        return Math.max(0, Math.min(100, ((props.value - props.min) / (props.max - props.min)) * 100))
    })

    const backgroundArcPath = computed(() => {
        // Calculate the arc path for the gauge
        const radius = 80
        const startAngle = -180
        const endAngle = 0
        const angleRange = endAngle - startAngle
        const valueAngle = angleRange + startAngle

        // Convert angles to radians
        const startRad = (startAngle * Math.PI) / 180
        const valueRad = (valueAngle * Math.PI) / 180

        // Calculate points
        const startX = 100 + radius * Math.cos(startRad)
        const startY = 100 + radius * Math.sin(startRad)
        const valueX = 100 + radius * Math.cos(valueRad)
        const valueY = 100 + radius * Math.sin(valueRad)

        // Create SVG path
        const largeArcFlag = valueAngle - startAngle <= 180 ? '0' : '1'

        return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${valueX} ${valueY}`
    })

    const arcPath = computed(() => {
        // Calculate the arc path for the gauge
        const radius = 80
        const startAngle = -180
        const endAngle = 0
        const angleRange = endAngle - startAngle
        const valueAngle = (angleRange * percentage.value) / 100 + startAngle

        // Convert angles to radians
        const startRad = (startAngle * Math.PI) / 180
        const valueRad = (valueAngle * Math.PI) / 180

        // Calculate points
        const startX = 100 + radius * Math.cos(startRad)
        const startY = 100 + radius * Math.sin(startRad)
        const valueX = 100 + radius * Math.cos(valueRad)
        const valueY = 100 + radius * Math.sin(valueRad)

        // Create SVG path
        const largeArcFlag = valueAngle - startAngle <= 180 ? '0' : '1'

        return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${valueX} ${valueY}`
    })

    // Allow overriding base classes
    const gaugeBaseClasses = 'w-full max-w-48 relative aspect-2/1'
    const gaugeMergedClasses = computed(() => twMerge(gaugeBaseClasses, props.class))

    const textBaseClasses = 'text-3xl font-semibold absolute bottom-0 left-1/2 -translate-x-1/2'
    const textMergedClasses = computed(() => twMerge(textBaseClasses, options.value.textClass))
</script>
